import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  detectLocaleFromHeader,
  LOCALE_COOKIE_NAME,
  makeT,
  readLocaleCookie,
  type Locale,
  type TFn,
} from "@bloom/i18n";

interface LocaleCtx {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: TFn;
}

const LocaleContext = createContext<LocaleCtx | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof document === "undefined") return "en";
    const fromCookie = readLocaleCookie(document.cookie);
    if (fromCookie) return fromCookie;
    return detectLocaleFromHeader(navigator.language);
  });

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    const expires = new Date(Date.now() + 60 * 60 * 24 * 365 * 1000).toUTCString();
    document.cookie = `${LOCALE_COOKIE_NAME}=${next}; expires=${expires}; path=/; SameSite=Lax`;
  }, []);

  const t = useMemo(() => makeT(locale), [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleCtx {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
